import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api, apiError, PUBLIC_HOST } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  TrophyIcon,
  SealCheckIcon,
  ShoppingBagIcon,
  CrownIcon,
  CopyIcon,
  ShareNetworkIcon,
  ArrowSquareOutIcon,
  WarningIcon,
} from "@phosphor-icons/react";

/**
 * Recruit — earn points and credit by inviting friends. Live dashboard
 * showing the user's code, share-link, milestone rewards, monthly +
 * all-time rank, and a tap-to-share sheet for SMS / X / WhatsApp / copy.
 *
 * Rewards lock until the recruiter is themselves ID-verified — we
 * surface that explicitly at the top so users know the unlock criterion
 * upfront (no "where's my credit?" support tickets).
 */
export default function Recruit() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for the AuthContext to finish its initial /auth/me check before
    // deciding whether to redirect — without this, a direct nav to /refer
    // races the bootstrap and bounces logged-in users to /login.
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    let alive = true;
    (async () => {
      try {
        const [me, list] = await Promise.all([
          api.get("/me/recruit"),
          api.get("/me/recruit/applicants"),
        ]);
        if (!alive) return;
        setData(me.data);
        setApplicants(list.data?.items || []);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return null;

  const shareUrl =
    data?.code ? `${PUBLIC_HOST}/r/${data.code}` : "";
  const shareText = `Come build with me on Bidorah — verified live auctions, no fakes, real bids. Use my invite to skip the line:`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url: shareUrl, title: "Bidorah invite" });
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="recruit-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <p className="overline text-zinc-500">Recruit & earn</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1 text-zinc-950">
          Bring real ones. <span className="text-[#1D78AB]">Get paid.</span>
        </h1>
        <p className="mt-3 text-zinc-600 max-w-2xl leading-relaxed">
          Share your invite. The moment someone you brought hits a milestone, points
          and platform credit land in your account. Top recruiters take home cash
          prizes monthly.
        </p>

        {loading ? (
          <div className="mt-12 text-zinc-500">Loading…</div>
        ) : (
          <>
            {data?.rewards_locked && (
              <div
                className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-4"
                data-testid="recruit-locked-banner"
              >
                <WarningIcon size={22} className="text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <div className="font-bold">Verify your ID to unlock rewards.</div>
                  Your invite link still works and signups are tracked, but points
                  and credit only flow once you're ID-verified.{" "}
                  <Link to="/verify" className="underline font-semibold">
                    Verify now →
                  </Link>
                </div>
              </div>
            )}

            {/* SHARE CARD — code, link, copy, native share */}
            <div className="mt-8 rounded-2xl border border-zinc-200 bg-zinc-950 text-white p-6 sm:p-8">
              <div className="overline text-zinc-400 mb-3">Your invite code</div>
              <div className="flex flex-wrap items-end gap-4">
                <div
                  className="font-mono text-5xl sm:text-7xl font-black tracking-[0.18em] text-amber-300 select-all"
                  data-testid="recruit-code"
                >
                  {data?.code}
                </div>
                <div className="text-sm text-zinc-400 pb-2">
                  Anyone who signs up with this code is permanently linked to you.
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div
                  className="font-mono text-xs sm:text-sm bg-white/5 border border-white/10 rounded-full px-4 py-2 truncate flex-1 min-w-[200px]"
                  data-testid="recruit-share-url"
                >
                  {shareUrl || "Loading…"}
                </div>
                <Button
                  onClick={copy}
                  className="bg-amber-300 text-amber-950 hover:bg-amber-200 rounded-full font-bold gap-2"
                  data-testid="recruit-copy-btn"
                >
                  <CopyIcon size={14} weight="bold" /> Copy
                </Button>
                <Button
                  onClick={nativeShare}
                  variant="outline"
                  className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 gap-2"
                  data-testid="recruit-share-btn"
                >
                  <ShareNetworkIcon size={14} weight="bold" /> Share
                </Button>
              </div>

              {/* Quick-share targets */}
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-zinc-500 mr-1">Or post to:</span>
                <a
                  className="px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="recruit-share-x"
                >
                  𝕏 / Twitter
                </a>
                <a
                  className="px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                  href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="recruit-share-wa"
                >
                  WhatsApp
                </a>
                <a
                  className="px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                  href={`sms:?&body=${encodeURIComponent(shareText + " " + shareUrl)}`}
                  data-testid="recruit-share-sms"
                >
                  SMS
                </a>
                <a
                  className="px-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                  href={`mailto:?subject=${encodeURIComponent("Come build with me on Bidorah")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`}
                  data-testid="recruit-share-email"
                >
                  Email
                </a>
              </div>
            </div>

            {/* STATS GRID */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="recruit-stats-grid">
              <Stat label="Points" value={data?.stats?.total_points || 0} />
              <Stat
                label="Credit"
                value={`$${((data?.stats?.total_credit_cents || 0) / 100).toFixed(2)}`}
              />
              <Stat label="Verified" value={data?.stats?.verified_count || 0} />
              <Stat
                label="Rank · this month"
                value={data?.rank_month ? `#${data.rank_month}` : "—"}
              />
            </div>

            {/* TIERS — visualize what each milestone pays */}
            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl text-zinc-950">
                What you earn per milestone
              </h2>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                <Tier
                  icon={<SealCheckIcon size={22} weight="fill" className="text-emerald-600" />}
                  title="ID-verified"
                  copy="Your friend uploads a US ID and gets approved."
                  points={50}
                  credit="$2.00"
                />
                <Tier
                  icon={<ShoppingBagIcon size={22} weight="fill" className="text-[#1D78AB]" />}
                  title="First purchase"
                  copy="Their first auction win or Buy-Now ships and is delivered."
                  points={200}
                  credit="$10.00"
                />
                <Tier
                  icon={<CrownIcon size={22} weight="fill" className="text-amber-500" />}
                  title="Founder Seller"
                  copy="They become an approved Founder Seller. Big payday."
                  points={1000}
                  credit="$50.00"
                  hot
                />
              </div>
            </div>

            {/* LEADERBOARD CTA */}
            <div className="mt-10 flex items-center justify-between rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
              <div className="flex items-center gap-3">
                <TrophyIcon size={28} weight="fill" className="text-amber-500" />
                <div>
                  <div className="font-display font-bold text-lg text-zinc-950">
                    Top recruiters get paid in cash.
                  </div>
                  <div className="text-sm text-zinc-600">
                    Top 3 each month split a $500 cash bonus on top of their points.
                  </div>
                </div>
              </div>
              <Link to="/leaderboard" data-testid="recruit-leaderboard-cta">
                <Button variant="outline" className="rounded-full gap-2">
                  Leaderboard <ArrowSquareOutIcon size={14} weight="bold" />
                </Button>
              </Link>
            </div>

            {/* MY APPLICANTS */}
            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl text-zinc-950">
                Your invites
              </h2>
              {applicants.length === 0 ? (
                <p className="text-sm text-zinc-500 mt-3">
                  No one's signed up with your code yet. Share it and watch this
                  list (and the leaderboard) light up.
                </p>
              ) : (
                <div className="mt-4 border border-zinc-200 rounded-xl overflow-hidden" data-testid="recruit-applicants-list">
                  {applicants.map((a) => (
                    <ApplicantRow key={a.id} a={a} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="overline text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl sm:text-3xl text-zinc-950 mt-1 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function Tier({ icon, title, copy, points, credit, hot }) {
  return (
    <div
      className={`rounded-xl border p-5 ${
        hot ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"
      }`}
    >
      <div className="flex items-center gap-2">{icon}<div className="font-display font-bold text-zinc-950">{title}</div></div>
      <p className="text-xs text-zinc-600 mt-2 leading-snug">{copy}</p>
      <div className="mt-4 flex items-baseline gap-3">
        <span className="font-mono text-2xl font-black text-zinc-950">{points}</span>
        <span className="overline text-zinc-500">points</span>
      </div>
      <div className="mt-1 text-sm font-semibold text-emerald-700">+ {credit} credit</div>
    </div>
  );
}

function ApplicantRow({ a }) {
  const milestones = a.milestones || {};
  const reached = (k) => !!milestones[k];
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100 last:border-b-0 bg-white">
      <div className="h-9 w-9 rounded-full bg-zinc-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        {a.applicant_picture ? (
          <img src={a.applicant_picture} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-zinc-500">?</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-zinc-950 truncate">
          @{a.applicant_username || "—"}
        </div>
        <div className="text-[11px] text-zinc-500">
          Joined {new Date(a.signup_at).toLocaleDateString()}
          {a.fraud_flags?.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-semibold">
              {a.status === "manual_review" ? "Under review" : a.status}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-[10px]">
        <Step on={reached("verified")} label="Verified" />
        <Step on={reached("first_purchase")} label="Bought" />
        <Step on={reached("founder_seller")} label="Founder" />
      </div>
      <div className="ml-3 text-right tabular-nums">
        <div className="font-mono font-bold text-zinc-950 text-sm">{a.total_points || 0} pts</div>
        <div className="text-[11px] text-emerald-700 font-semibold">
          ${((a.total_credit_cents || 0) / 100).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

function Step({ on, label }) {
  return (
    <span
      className={`px-2 py-1 rounded-full font-bold tracking-wide ${
        on ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-400"
      }`}
    >
      {label}
    </span>
  );
}
